import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { FundingRequestsHistoryRoute } from 'borrower/routes/funding-requests/FundingRequestsHistoryRoute';
import { FundingRequestsLandingRoute } from 'borrower/routes/funding-requests/FundingRequestsLandingRoute';
import { FundingRequestsRoute } from 'borrower/routes/funding-requests/FundingRequestsRoute';
import { FundingRequestHistoryWithVerificationRoute } from 'packages/funding-requests/routes/FundingRequestHistoryWithVerificationRoute';
import { VerificationEligibilityRoute } from 'packages/funding-requests/routes/VerificationEligibilityRoute';
import { VerificationHistoryRoute } from 'packages/funding-requests/routes/VerificationHistoryRoute';
import { VerificationWorkflowRoute } from 'packages/funding-requests/routes/VerificationWorkflowRoute';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Types
const RouteParamsStruct = z.object({
  fundingRequestId: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
const RedirectFromApprove = () => {
  const params = useParams();
  const { fundingRequestId } = RouteParamsStruct.parse(params);
  return (
    <Navigate
      to={'/funding-requests/:fundingRequestId'.replace(
        ':fundingRequestId',
        fundingRequestId,
      )}
    />
  );
};

// Exports
export const FundingRequestRouter = () => {
  const flags = useFeatureFlags();

  return (
    // Paths relative to "/funding-requests/*"
    <Routes>
      {/* --- Funding request landing route --- */}
      <Route
        path="/"
        element={
          flags.isBorrowerLimitedOrganization ? (
            <FundingRequestHistoryWithVerificationRoute enableVerifications />
          ) : (
            <FundingRequestsLandingRoute />
          )
        }
      />

      {/* --- Funding verification routes --- */}
      {flags.isBorrowerLimitedOrganization && (
        <Route
          path="/verifications/new"
          element={<VerificationWorkflowRoute />}
        />
      )}
      {flags.isBorrowerLimitedOrganization && (
        <Route
          path="/verifications/pipeline/history"
          element={<VerificationHistoryRoute />}
        />
      )}
      {flags.isBorrowerLimitedOrganization && (
        <Route
          path="/verifications/pipeline/:artifactDocumentId/:creditFacilityId/:reportingDate/:fileVersion"
          element={<VerificationWorkflowRoute />}
        />
      )}
      {flags.isBorrowerLimitedOrganization && (
        <Route
          path="/verifications/pipeline/:artifactDocumentId/:creditFacilityId/:reportingDate/:fileVersion/:verificationReportId/eligibility"
          element={<VerificationEligibilityRoute />}
        />
      )}

      {/* --- Funding request workflow routes --- */}
      <Route
        path="/history"
        element={
          !flags.isBorrowerLimitedOrganization ? (
            <FundingRequestsHistoryRoute />
          ) : (
            <Navigate to="/funding-requests" />
          )
        }
      />

      {/* --- Individual funding request routes --- */}
      <Route path="/:fundingRequestId" element={<FundingRequestsRoute />} />
      <Route
        path="/:fundingRequestId/approve/*"
        element={<RedirectFromApprove />}
      />

      <Route path="*" element={<Navigate to="/funding-requests" />} />
    </Routes>
  );
};
